// src/modules/app-views/services/pages/Init/modals/DataImportModal/StepUpload.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Upload, Card, Descriptions, Space, Spin, Typography } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const ACCEPTED_TYPES = '.csv,.xls,.xlsx,.json,.pdf,.png,.jpg,.jpeg,.webp';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const StepUploadContent = ({ uploading, fileInfo, onUpload }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <Text type="secondary">{translateX('data_import_upload_description')}</Text>

      <div style={{ marginTop: 16 }}>
        <Upload.Dragger
          name="file"
          multiple={false}
          beforeUpload={onUpload}
          accept={ACCEPTED_TYPES}
          showUploadList={false}
          disabled={uploading}
        >
          <div className="ant-upload-drag-icon">
            {uploading
              ? <Spin size="large" />
              : <UploadOutlined style={{ fontSize: 40, color: '#1677ff' }} />
            }
          </div>
          <div className="ant-upload-text">
            {uploading ? translateX('processing') + '...' : translateX('data_import_select_file')}
          </div>
          <div className="ant-upload-hint">{translateX('data_import_accepted_formats')}</div>
        </Upload.Dragger>
      </div>

      {fileInfo && (
        <Card size="small" style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label={translateX('file')}>
              <Space>
                <FileOutlined />
                {fileInfo.originalName}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={translateX('records')}>
              {fileInfo.totalRows} {translateX('data_import_rows_detected')}
            </Descriptions.Item>
            <Descriptions.Item label={translateX('columns')}>
              {fileInfo.headers?.length} {translateX('data_import_columns_detected')}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const stepUpload = ({ uploading, fileInfo, onUpload, importIdRef, onGoNextReady }) => ({
  stepTitle: 'upload',
  stepTooltip: 'data_import_select_file',
  content: ({ goNext }) => {
    onGoNextReady?.(goNext);
    return (
      <StepUploadContent
        uploading={uploading}
        fileInfo={fileInfo}
        onUpload={(file) => onUpload(file, goNext)}
      />
    );
  },
  // Validate that a file was uploaded before advancing (manual Next click guard)
  onNext: async () => {
    if (!importIdRef?.current) {
      appService.message({ flag: 'w', text: 'data_import_select_file' });
      return false;
    }
    return true;
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default stepUpload;
